import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight, LuFlame } from 'react-icons/lu';
import { IconButton } from '../../ui/Button';
import { Skeleton } from '../../ui/Feedback';
import { useStrings } from '../../tenant/TenantProvider';
import { practiceApi } from '../../lib/practiceApi';
import { formatMonthYear } from '../../lib/format';
import { cx } from '../../ui/cx';

const WEEKDAY_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'narrow', timeZone: 'UTC' });
const WEEKDAYS = Array.from({ length: 7 }, (_, index) => WEEKDAY_FORMAT.format(new Date(Date.UTC(2023, 0, 1 + index))));
const MONTH_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });

const isoDay = (year, month, day) => new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
const monthKey = (year, month) => `${year}-${month}`;

export function StreakCalendar({ daily }) {
  const t = useStrings();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => (daily ? { year: daily.year, month: daily.month } : null));
  const [months, setMonths] = useState({});

  useEffect(() => {
    if (!daily) return;
    setCursor((current) => current ?? { year: daily.year, month: daily.month });
    setMonths((current) => ({ ...current, [monthKey(daily.year, daily.month)]: daily }));
  }, [daily]);

  const key = cursor ? monthKey(cursor.year, cursor.month) : null;
  const data = key ? months[key] : null;

  useEffect(() => {
    if (!cursor || months[key]) return undefined;
    let cancelled = false;
    practiceApi.daily(cursor.year, cursor.month).then((response) => {
      if (!cancelled) setMonths((current) => ({ ...current, [key]: response.success ? response.data : { calendar: [], maxStreak: 0 } }));
    });
    return () => {
      cancelled = true;
    };
  }, [key, cursor, months]);

  const entries = useMemo(() => Object.fromEntries((data?.calendar ?? []).map((entry) => [entry.date, entry])), [data]);

  if (!daily || !cursor) {
    return (
      <section className="panel streak-calendar" aria-busy="true">
        <div className="panel__body">
          <Skeleton width="50%" />
          <Skeleton width="100%" height={200} style={{ marginTop: 16 }} />
        </div>
      </section>
    );
  }

  const isCurrentMonth = cursor.year === daily.year && cursor.month === daily.month;
  const firstDay = new Date(Date.UTC(cursor.year, cursor.month - 1, 1));
  const daysInMonth = new Date(Date.UTC(cursor.year, cursor.month, 0)).getUTCDate();
  const monthLabel = MONTH_FORMAT.format(firstDay);

  const move = (delta) => {
    setCursor(({ year, month }) => {
      const next = month + delta;
      if (next < 1) return { year: year - 1, month: 12 };
      if (next > 12) return { year: year + 1, month: 1 };
      return { year, month: next };
    });
  };

  return (
    <section className="panel streak-calendar" aria-labelledby="streak-title">
      <div className="panel__header">
        <h2 id="streak-title" className="panel__title">
          {t('problems.streak.title')}
        </h2>
        <div className="streak-calendar__nav">
          <IconButton size="sm" icon={LuChevronLeft} label={t('problems.streak.previousMonth')} onClick={() => move(-1)} />
          <span className="streak-calendar__month" aria-live="polite">
            {monthLabel}
          </span>
          <IconButton size="sm" icon={LuChevronRight} label={t('problems.streak.nextMonth')} onClick={() => move(1)} disabled={isCurrentMonth} />
        </div>
      </div>
      <div className="panel__body">
        <div className="streak-calendar__grid" aria-label={formatMonthYear(firstDay)}>
          {WEEKDAYS.map((day, index) => (
            <span key={index} className="streak-calendar__weekday" aria-hidden="true">
              {day}
            </span>
          ))}
          {Array.from({ length: firstDay.getUTCDay() }, (_, index) => (
            <span key={`blank-${index}`} aria-hidden="true" />
          ))}
          {Array.from({ length: daysInMonth }, (_, index) => {
            const date = isoDay(cursor.year, cursor.month, index + 1);
            const entry = entries[date];
            const isToday = date === daily.todayIso;
            const state = entry ? (entry.solved ? 'solved' : date < daily.todayIso ? 'missed' : isToday ? 'today' : 'upcoming') : date > daily.todayIso ? 'future' : 'empty';
            const label = entry
              ? t(`problems.streak.${state === 'solved' ? 'solvedDay' : state === 'missed' ? 'missedDay' : state === 'today' ? 'todayDay' : 'upcomingDay'}`, {
                  title: entry.title,
                })
              : undefined;
            const className = cx('streak-day', `streak-day--${state}`, isToday && 'is-today');
            return entry ? (
              <button
                key={date}
                type="button"
                className={className}
                title={label}
                aria-label={`${index + 1}. ${label}`}
                onClick={() => navigate(`/problems/${entry.questionId}`, { viewTransition: true })}
              >
                {index + 1}
              </button>
            ) : (
              <span key={date} className={className}>
                {index + 1}
              </span>
            );
          })}
        </div>
        {data ? (
          <dl className="streak-calendar__stats">
            {isCurrentMonth && (
              <div>
                <dt>{t('problems.streak.current')}</dt>
                <dd>
                  <LuFlame aria-hidden="true" className={cx('streak-calendar__flame', daily.streak > 0 && 'is-lit')} />
                  {t('problems.streak.days', { count: daily.streak })}
                </dd>
              </div>
            )}
            <div>
              <dt>{t('problems.streak.best')}</dt>
              <dd>{t('problems.streak.days', { count: data.maxStreak })}</dd>
            </div>
          </dl>
        ) : (
          <Skeleton width="60%" style={{ marginTop: 16 }} />
        )}
      </div>
    </section>
  );
}
